const fontFamily = '"Inter", "Segoe UI", Arial, sans-serif'

const lightColor = {
	canvas: '#fbfbfa', canvasSubtle: '#f7f7f5', navigation: '#f5f5f2', surface: '#ffffff', surfaceMuted: '#f1f1ee',
	text: '#242424', textSecondary: '#686762', textDisabled: '#a09f9a',
	border: 'rgba(55, 53, 47, 0.14)', borderStrong: 'rgba(55, 53, 47, 0.24)', hover: 'rgba(55, 53, 47, 0.06)',
	selected: 'rgba(23, 105, 170, 0.10)', focusRing: 'rgba(23, 105, 170, 0.18)', backdrop: 'rgba(31, 35, 40, 0.28)',
	scrollbarTrack: 'rgba(55, 53, 47, 0.04)', scrollbarThumb: 'rgba(55, 53, 47, 0.26)', scrollbarThumbHover: 'rgba(55, 53, 47, 0.40)',
	primary: '#1769aa', primaryDark: '#0f4f82', primaryLight: '#eaf4fb', secondary: '#238ca6', secondaryDark: '#176879', secondaryLight: '#e7f6f8',
	success: '#2f7d5b', warning: '#9a6700', error: '#c43d3d', info: '#3977a8',
}

const darkColor = {
	canvas: '#181a1b', canvasSubtle: '#1d2021', navigation: '#1c1f20', surface: '#242728', surfaceMuted: '#2b2f30',
	text: '#f1f1ec', textSecondary: '#b7b8b0', textDisabled: '#777a74',
	border: 'rgba(235, 236, 229, 0.14)', borderStrong: 'rgba(235, 236, 229, 0.24)', hover: 'rgba(235, 236, 229, 0.07)',
	selected: 'rgba(79, 163, 211, 0.16)', focusRing: 'rgba(79, 163, 211, 0.28)', backdrop: 'rgba(0, 0, 0, 0.52)',
	scrollbarTrack: 'rgba(235, 236, 229, 0.05)', scrollbarThumb: 'rgba(235, 236, 229, 0.30)', scrollbarThumbHover: 'rgba(235, 236, 229, 0.44)',
	primary: '#4fa3d3', primaryDark: '#2b78aa', primaryLight: '#16384d', secondary: '#58b7c9', secondaryDark: '#338798', secondaryLight: '#153b43',
	success: '#63b78b', warning: '#d6a75a', error: '#e27373', info: '#70b7dd',
}

const radius = { xs: 4, sm: 6, md: 7, lg: 10, round: 999 }
const control = { height: 36, heightSmall: 30, chipHeight: 24, horizontalPadding: 12 }
const shadow = {
	surface: '0 1px 2px rgba(15, 23, 42, 0.04)',
	overlay: '0 8px 24px rgba(15, 23, 42, 0.10), 0 1px 3px rgba(15, 23, 42, 0.08)',
}
const transition = { fast: '120ms ease', standard: '180ms ease' }

function createReldoTokens(color) {
	return {
		color,
		fontFamily,
		radius,
		control,
		border: { default: `1px solid ${color.border}`, strong: `1px solid ${color.borderStrong}` },
		shadow,
		transition,
	}
}

function createReldoPalette(color, mode) {
	const contrastText = mode === 'dark' ? '#071317' : '#ffffff'
	return {
		mode,
		primary: { main: color.primary, dark: color.primaryDark, light: color.primaryLight, contrastText },
		secondary: { main: color.secondary, dark: color.secondaryDark, light: color.secondaryLight, contrastText },
		success: { main: color.success }, warning: { main: color.warning }, error: { main: color.error }, info: { main: color.info },
		background: { default: color.canvas, paper: color.surface },
		text: { primary: color.text, secondary: color.textSecondary, disabled: color.textDisabled },
		divider: color.border,
		action: {
			active: color.textSecondary, hover: color.hover, selected: color.selected, disabled: color.textDisabled,
			disabledBackground: color.surfaceMuted, disabledOpacity: 0.52, focus: color.focusRing,
		},
	}
}

export const reldoLightTokens = createReldoTokens(lightColor)
export const reldoDarkTokens = createReldoTokens(darkColor)
export const reldoTokens = reldoLightTokens

export const reldoColorSchemes = {
	light: { palette: createReldoPalette(lightColor, 'light'), reldo: reldoLightTokens },
	dark: { palette: createReldoPalette(darkColor, 'dark'), reldo: reldoDarkTokens },
}

export const reldoTypography = {
	fontFamily,
	fontSize: 14,
	h1: { fontSize: '2rem', lineHeight: 1.2, fontWeight: 650, letterSpacing: '-0.025em' },
	h2: { fontSize: '1.75rem', lineHeight: 1.25, fontWeight: 650, letterSpacing: '-0.02em' },
	h3: { fontSize: '1.5rem', lineHeight: 1.3, fontWeight: 650, letterSpacing: '-0.015em' },
	h4: { fontSize: '1.25rem', lineHeight: 1.35, fontWeight: 650, letterSpacing: '-0.01em' },
	h5: { fontSize: '1.125rem', lineHeight: 1.4, fontWeight: 600 }, h6: { fontSize: '1rem', lineHeight: 1.45, fontWeight: 600 },
	subtitle1: { fontSize: '0.9375rem', lineHeight: 1.5, fontWeight: 600 }, subtitle2: { fontSize: '0.8125rem', lineHeight: 1.45, fontWeight: 600 },
	body1: { fontSize: '0.875rem', lineHeight: 1.55 }, body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
	button: { fontSize: '0.8125rem', lineHeight: 1.4, fontWeight: 600, textTransform: 'none' },
	caption: { fontSize: '0.75rem', lineHeight: 1.45 }, overline: { fontSize: '0.6875rem', lineHeight: 1.5, fontWeight: 600, letterSpacing: '0.06em' },
}

export const muiShadows = Array.from({ length: 25 }, (_, index) => {
	if (index === 0) return 'none'
	return index < 8 ? shadow.surface : shadow.overlay
})
